import classNames from "classnames";
import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { usePermissionsApi } from "../../http/permissions/api";
import styles from "./MultiCheckboxPermission.module.css";

interface MultiCheckboxPermissionProps {
  isDisabled?: boolean;
  onPermissionRowClick: (permissions: string[], resource: string) => void;
  isCheckBoxDisabled: (permmissions: string[], permissionPrefix: string, permissionAction: string) => boolean;
  isCheckBoxChecked: (permmission: string) => boolean;
  onCheckBoxChange: (e: React.ChangeEvent<HTMLInputElement>, permission: string) => void;
  showClients: boolean;
}

const MultiCheckboxPermission: FC<MultiCheckboxPermissionProps> = ({
  isDisabled,
  onPermissionRowClick,
  isCheckBoxDisabled,
  isCheckBoxChecked,
  onCheckBoxChange,
  showClients,
}: MultiCheckboxPermissionProps) => {
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const i18n = useI18n();
  const permissionsApi = usePermissionsApi();

  const dataPermissions = permissionsApi.useAdminPermissions(aborter.current.signal);

  useEffect(() => {
    dataPermissions.error && dispatch(showErrorNotification(i18n, dataPermissions.error));
  }, [dataPermissions.error]);

  const [permissions] = useDataInitializer(dataPermissions?.data);

  const tableRows = permissions?.reduce((rv, perm) => {
    const [resource, operation] = perm.split(":");
    if (resource && operation) {
      if (resource && !rv.has(resource)) {
        rv.set(resource, []);
      }

      rv.get(resource)?.push(operation);
    }
    return rv;
  }, new Map<string, string[]>());

  return (
    <table className={styles["multi-check-box"]}>
      <thead>
        <tr>
          <th></th>
          <th>{i18n.t.common.read}</th>
          <th>{i18n.t.common.insert}</th>
          <th>{i18n.t.common.update}</th>
          <th>{i18n.t.common.delete}</th>
        </tr>
      </thead>
      <tbody>
        {[...(tableRows?.entries() ?? [])].map(([resource, permissions]) => {
          if (resource === "clients" && !showClients) return;

          const isReadDisabled = isDisabled || isCheckBoxDisabled(permissions, resource, "read");
          const isInsertDisabled = isDisabled || isCheckBoxDisabled(permissions, resource, "insert");
          const isUpdateDisabled = isDisabled || isCheckBoxDisabled(permissions, resource, "update");
          const isDeleteDisabled = isDisabled || isCheckBoxDisabled(permissions, resource, "delete");

          const isRowEnabled = !isReadDisabled || !isInsertDisabled || !isUpdateDisabled || !isDeleteDisabled;

          return (
            <tr key={resource} className={styles["permission-row"]}>
              <td className={classNames(styles.title, { [styles.enabled]: isRowEnabled })}>
                <div onClick={() => onPermissionRowClick(permissions, resource)}>{resource}</div>
              </td>
              <td align="center">
                <input
                  type="checkbox"
                  disabled={isReadDisabled}
                  onChange={(e) => onCheckBoxChange(e, resource + ":read")}
                  checked={isCheckBoxChecked(resource + ":read")}
                />
              </td>
              <td align="center">
                <input
                  type="checkbox"
                  disabled={isInsertDisabled}
                  onChange={(e) => onCheckBoxChange(e, resource + ":insert")}
                  checked={isCheckBoxChecked(resource + ":insert")}
                />
              </td>
              <td align="center">
                <input
                  type="checkbox"
                  disabled={isUpdateDisabled}
                  onChange={(e) => onCheckBoxChange(e, resource + ":update")}
                  checked={isCheckBoxChecked(resource + ":update")}
                />
              </td>
              <td align="center">
                <input
                  type="checkbox"
                  disabled={isDeleteDisabled}
                  onChange={(e) => onCheckBoxChange(e, resource + ":delete")}
                  checked={isCheckBoxChecked(resource + ":delete")}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MultiCheckboxPermission;
